import { useEffect, useMemo, useState } from "react"
import { router, Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Radio } from "@/components/ui/radio"
import { Checkbox } from "@/components/ui/checkbox"
import { useFormatDateTime, useUserTimezone } from "@/lib/datetime"
import { labelForChannelType } from "@/lib/channel-icons"
import { cn } from "@/lib/utils"

type Mode = "next" | "pick" | "custom"

type Props = {
  open: boolean
  onClose: () => void
  pieceId: number
  channelSlug: string
  channelType: string
  // "connected" | "not_connected" | "expired" — drives the
  // "Publish manually" checkbox default + disabled state.
  channelStatus: string
  // Capability flags for platform scheduling — true when the channel
  // type supports the platform's own scheduler (youtube_shorts, newsletter).
  // When all three of (supports + allows OR forces) hold, the dialog
  // surfaces a "Use platform scheduling" toggle and the dual upload+publish
  // datetime inputs.
  channelSupportsPlatformScheduling: boolean
  channelAllowsPlatformScheduling: boolean
  channelForcesPlatformScheduling: boolean
  channelDefaultPlatformScheduling: boolean
  channelLeadHours: number
  // YouTube only: current piece visibility, so the dialog can block
  // platform scheduling for unlisted/private (which YouTube's publishAt
  // API can't honor).
  pieceVisibility?: string | null
  // Pre-fill for Reschedule: current slot's publish_at + manual_publish.
  initialScheduledAt?: string | null
  initialManualPublish?: boolean
  initialUploadAt?: string | null
  initialUsePlatformScheduling?: boolean
}

// Unified scheduling modal — covers the simple single-time flow plus the
// platform-scheduling dual-time flow (upload_at + publish_at).
export function ScheduleDialog({
  open,
  onClose,
  pieceId,
  channelSlug,
  channelType,
  channelStatus,
  channelSupportsPlatformScheduling,
  channelAllowsPlatformScheduling,
  channelForcesPlatformScheduling,
  channelDefaultPlatformScheduling,
  channelLeadHours,
  pieceVisibility,
  initialScheduledAt,
  initialManualPublish,
  initialUploadAt,
  initialUsePlatformScheduling,
}: Props) {
  const fmt = useFormatDateTime()
  const tz  = useUserTimezone()

  const isConnected = channelStatus === "connected"
  const channelLabel = labelForChannelType(channelType)
  // YouTube long-form and YouTube Posts have no API integration — every
  // slot is manually published by the operator. Force the checkbox on and
  // disabled.
  const forcesManualPublish =
    channelType === "youtube_long_form" || channelType === "youtube_post"
  const isYoutubeChannel = channelType === "youtube_shorts" || channelType === "youtube_long_form"
  const visibilityBlocksPlatformScheduling =
    isYoutubeChannel && (pieceVisibility ?? "").toString() !== "public"

  // Platform-scheduling capability: visible when the channel type supports
  // it AND the operator has opted in (or it's forced). When forced (Kit
  // newsletter), the per-piece toggle is hidden and always-on.
  const platformSchedulingAvailable =
    channelSupportsPlatformScheduling &&
    (channelAllowsPlatformScheduling || channelForcesPlatformScheduling)
  const platformSchedulingForced = channelForcesPlatformScheduling

  const [mode, setMode] = useState<Mode>(initialScheduledAt ? "custom" : "next")
  const [slots, setSlots] = useState<string[]>([])
  const [pickedSlot, setPickedSlot] = useState<string | null>(null)
  const [publishDate, setPublishDate] = useState<string>("")
  const [publishTime, setPublishTime] = useState<string>("")
  const [uploadDate, setUploadDate] = useState<string>("")
  const [uploadTime, setUploadTime] = useState<string>("")
  const [manualPublish, setManualPublish] = useState<boolean>(
    forcesManualPublish ? true : (initialManualPublish ?? !isConnected),
  )
  const [usePlatformScheduling, setUsePlatformScheduling] = useState<boolean>(
    initialUsePlatformScheduling ??
      (platformSchedulingForced || (platformSchedulingAvailable && channelDefaultPlatformScheduling)),
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // When the channel isn't connected, force the manual-publish checkbox on.
  // When platform scheduling is on, manual-publish doesn't apply (the
  // platform handles dispatch). YouTube long-form and Posts are always manual.
  const manualForced = forcesManualPublish || (!isConnected && !usePlatformScheduling)

  // Seed defaults each time the dialog opens.
  useEffect(() => {
    if (!open) return
    setError(null)
    setManualPublish(forcesManualPublish ? true : (initialManualPublish ?? !isConnected))
    setUsePlatformScheduling(
      initialUsePlatformScheduling ??
        (platformSchedulingForced || (platformSchedulingAvailable && channelDefaultPlatformScheduling)),
    )

    if (initialScheduledAt) {
      const parts = localPartsForIso(initialScheduledAt, tz)
      setMode("custom")
      setPublishDate(parts.date)
      setPublishTime(parts.time)
    } else {
      setMode("next")
      // Fresh schedule: publish defaults to now + leadHours, or now + 24h
      // when the channel has no required lead time configured.
      const effectiveLeadHours = channelLeadHours > 0 ? channelLeadHours : 24
      const publishDef = localParts(new Date(Date.now() + effectiveLeadHours * 3600 * 1000), tz)
      setPublishDate(publishDef.date)
      setPublishTime(publishDef.time)
    }

    if (initialUploadAt) {
      const parts = localPartsForIso(initialUploadAt, tz)
      setUploadDate(parts.date)
      setUploadTime(parts.time)
    } else if (initialScheduledAt) {
      // Reschedule: if there was no upload_at, seed it lead_hours before
      // the publish_at so toggling the platform-scheduling switch on
      // produces a sensible default.
      const seed = new Date(new Date(initialScheduledAt).getTime() - channelLeadHours * 3600 * 1000)
      const parts = localParts(seed, tz)
      setUploadDate(parts.date)
      setUploadTime(parts.time)
    } else {
      // Fresh schedule: upload defaults to now + 10 min.
      const uploadDef = nowPlus10(tz)
      setUploadDate(uploadDef.date)
      setUploadTime(uploadDef.time)
    }
  }, [
    open,
    initialScheduledAt,
    initialManualPublish,
    initialUploadAt,
    initialUsePlatformScheduling,
    isConnected,
    tz,
    channelLeadHours,
    platformSchedulingForced,
    platformSchedulingAvailable,
    channelDefaultPlatformScheduling,
  ])

  // Lazy-load the open slots list whenever "Pick a slot…" is selected.
  useEffect(() => {
    if (!open || mode !== "pick") return
    setLoading(true)
    setError(null)
    fetch(`/scheduling/state/${channelSlug}?horizon_days=21`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data) => {
        const next = (data.open_slots ?? []).slice(0, 10) as string[]
        setSlots(next)
        setPickedSlot(next[0] ?? null)
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [open, mode, channelSlug])

  const resolvePublishAtIso = (): string | null => {
    if (mode === "pick") return pickedSlot
    if (mode === "custom") {
      try {
        return new Date(`${publishDate}T${publishTime}:00`).toISOString()
      } catch {
        return null
      }
    }
    return null // mode === "next" → server picks
  }

  const resolveUploadAtIso = (): string | null => {
    if (!usePlatformScheduling) return null
    if (mode === "custom") {
      try {
        return new Date(`${uploadDate}T${uploadTime}:00`).toISOString()
      } catch {
        return null
      }
    }
    // For "next" and "pick" modes, let the server compute upload_at from
    // the lead time. Returning null tells SlotAssignment to do the math.
    return null
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (usePlatformScheduling && visibilityBlocksPlatformScheduling) {
      setError(
        "YouTube can only schedule public videos. Change visibility to “public” on the piece, " +
          "or uncheck platform scheduling to upload as unlisted/private immediately.",
      )
      return
    }

    setSubmitting(true)

    const body: {
      slot?: {
        publish_at?: string
        upload_at?: string
        use_platform_scheduling?: boolean
        manual_publish: boolean
      }
    } = {
      slot: {
        manual_publish: usePlatformScheduling ? false : manualPublish,
        use_platform_scheduling: usePlatformScheduling,
      },
    }

    if (mode !== "next") {
      const publishIso = resolvePublishAtIso()
      if (!publishIso) {
        setSubmitting(false)
        setError("Pick a publish date and time.")
        return
      }
      body.slot!.publish_at = publishIso

      if (usePlatformScheduling && mode === "custom") {
        const uploadIso = resolveUploadAtIso()
        if (!uploadIso) {
          setSubmitting(false)
          setError("Pick an upload date and time.")
          return
        }
        body.slot!.upload_at = uploadIso

        // Client-side lead-time check so the user gets immediate
        // feedback instead of a round-trip 422.
        if (channelLeadHours > 0) {
          const deltaHours = (new Date(publishIso).getTime() - new Date(uploadIso).getTime()) / 3600_000
          if (deltaHours < channelLeadHours) {
            setSubmitting(false)
            setError(`Publish time must be at least ${channelLeadHours}h after the upload time.`)
            return
          }
        }
      }
    }

    const verb = initialScheduledAt ? "patch" : "post"
    router[verb](
      `/pieces/${pieceId}/slot`,
      body,
      {
        preserveScroll: true,
        onSuccess: () => {
          setSubmitting(false)
          onClose()
        },
        onError: () => setSubmitting(false),
      },
    )
  }

  const slotOptions = useMemo(() => slots.map((iso) => ({ iso, label: fmt(iso) })), [slots, fmt])
  const dialogTitle = initialScheduledAt ? "Reschedule" : "Schedule"

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>
            {usePlatformScheduling
              ? `Pick when SparkDrop should upload to ${channelLabel} and when the platform should publish it.`
              : "Pick when this piece publishes. Manual-publish slots still hold the time on the channel calendar so the agent factors them into spreading."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-col gap-4">
          {platformSchedulingAvailable && (
            <div className="flex items-start gap-3 rounded-md border border-hairline bg-surface p-3">
              <Checkbox
                checked={usePlatformScheduling}
                disabled={platformSchedulingForced}
                onChange={(e) => setUsePlatformScheduling(e.target.checked)}
                className="mt-1"
              />
              <div className="space-y-1">
                <p className="font-medium text-ink-display">Use {channelLabel} platform scheduling</p>
                <p className="text-xs text-ink-muted">
                  {platformSchedulingForced
                    ? `${channelLabel} always uses its own scheduler — pick both upload + publish times below.`
                    : `Upload to ${channelLabel} ahead of time and let the platform fire the actual publish.`}
                </p>
              </div>
            </div>
          )}

          <fieldset className="flex flex-col gap-2">
            <legend>Schedule mode</legend>

            <ModeOption
              name="schedule-mode"
              value="next"
              checked={mode === "next"}
              onSelect={() => setMode("next")}
              title="Next open slot"
              description="Server picks the earliest open template slot."
            />
            <ModeOption
              name="schedule-mode"
              value="pick"
              checked={mode === "pick"}
              onSelect={() => setMode("pick")}
              title="Pick a slot…"
              description="Choose from the next ten open template slots."
            />
            <ModeOption
              name="schedule-mode"
              value="custom"
              checked={mode === "custom"}
              onSelect={() => setMode("custom")}
              title="Set a custom time…"
              description="Off-template times are tagged as a manual override."
            />
          </fieldset>

          {mode === "pick" && (
            <div className="rounded-md border border-hairline p-3">
              {loading && <p className="text-sm text-ink-muted">Loading slots…</p>}
              {error && <p className="text-sm text-danger">Couldn't load slots: {error}</p>}
              {!loading && !error && slotOptions.length === 0 && (
                <p className="text-sm text-ink-muted">No open slots in the horizon. Try a custom time.</p>
              )}
              {slotOptions.length > 0 && (
                <ul className="flex flex-col gap-1">
                  {slotOptions.map(({ iso, label }) => (
                    <li key={iso}>
                      <label className="flex items-center gap-2 rounded-sm px-1 py-1 font-normal text-ink-body hover:bg-surface">
                        <Radio
                          name="picked-slot"
                          value={iso}
                          checked={pickedSlot === iso}
                          onChange={() => setPickedSlot(iso)}
                        />
                        <span>{label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {mode === "custom" && (
            <div className="space-y-3">
              {usePlatformScheduling && (
                <div className="grid grid-cols-2 gap-3 rounded-md border border-hairline p-3">
                  <p className="col-span-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    Upload to {channelLabel}
                  </p>
                  <div>
                    <label htmlFor="sched-upload-date">Date</label>
                    <Input id="sched-upload-date" type="date" value={uploadDate} onChange={(e) => setUploadDate(e.target.value)} required />
                  </div>
                  <div>
                    <label htmlFor="sched-upload-time">Time</label>
                    <Input id="sched-upload-time" type="time" value={uploadTime} onChange={(e) => setUploadTime(e.target.value)} required />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 rounded-md border border-hairline p-3">
                {usePlatformScheduling && (
                  <p className="col-span-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    Publish on {channelLabel}
                  </p>
                )}
                <div>
                  <label htmlFor="sched-date">Date</label>
                  <Input id="sched-date" type="date" value={publishDate} onChange={(e) => setPublishDate(e.target.value)} required />
                </div>
                <div>
                  <label htmlFor="sched-time">Time</label>
                  <Input id="sched-time" type="time" value={publishTime} onChange={(e) => setPublishTime(e.target.value)} required />
                </div>
                <p className="col-span-2 text-xs text-ink-muted">
                  Times in {tz}
                  {usePlatformScheduling && channelLeadHours > 0 && ` · ${channelLeadHours}h minimum between upload and publish`}
                  .
                </p>
              </div>
            </div>
          )}

          {!usePlatformScheduling && (
            <div className="flex flex-col items-start gap-1.5 border-t border-hairline pt-3">
              <label className="flex items-center gap-2 font-normal text-ink-body">
                <Checkbox
                  checked={manualPublish}
                  disabled={manualForced}
                  onChange={(e) => setManualPublish(e.target.checked)}
                />
                <span>Publish manually</span>
              </label>
              {manualForced && (
                forcesManualPublish ? (
                  <span className="text-xs text-ink-muted">
                    {channelLabel} is always published manually.
                  </span>
                ) : (
                  <span className="text-xs text-ink-muted">
                    {channelLabel} isn't connected —{" "}
                    <Link href={`/settings/channels/${channelSlug}?tab=publishing`} className="link">
                      connect {channelLabel} account
                    </Link>
                  </span>
                )
              )}
            </div>
          )}

          {error && mode !== "pick" && <p className="text-sm text-danger">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : dialogTitle}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// One radio choice card. The whole card is the click target (it's wrapped
// in the <label>), the radio control sits inline with the title row so
// the dot lines up with the title baseline, and the selected card gets
// an accent border + faint accent tint to call out the active mode.
function ModeOption({
  name,
  value,
  checked,
  onSelect,
  title,
  description,
}: {
  name: string
  value: string
  checked: boolean
  onSelect: () => void
  title: string
  description: string
}) {
  return (
    <label
      className={cn(
        "block cursor-pointer rounded-md border p-3 transition-colors font-normal text-ink-body",
        checked
          ? "border-accent bg-accent-faded/40"
          : "border-hairline hover:border-accent/40",
      )}
    >
      <div className="flex items-center gap-2">
        <Radio name={name} value={value} checked={checked} onChange={onSelect} />
        <span className="font-medium text-ink-display">{title}</span>
      </div>
      <p className="mt-1 pl-6 text-xs text-ink-muted">{description}</p>
    </label>
  )
}

// Mirror of the legacy nowPlus10 helper.
function nowPlus10(tz: string): { date: string; time: string } {
  const target = new Date(Date.now() + 10 * 60 * 1000)
  return localParts(target, tz)
}

function localPartsForIso(iso: string, tz: string): { date: string; time: string } {
  return localParts(new Date(iso), tz)
}

function localParts(target: Date, tz: string): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone:  tz,
    year:      "numeric",
    month:     "2-digit",
    day:       "2-digit",
    hour:      "2-digit",
    minute:    "2-digit",
    hourCycle: "h23",
  }).formatToParts(target)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00"
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour")}:${get("minute")}`,
  }
}
